// Importar una planilla en Tienda online (ADR-2026-10-30-exportar-e-importar-en-su-pantalla). Antes de
// dársela al asistente se resuelve acá, sin IA, lo que piden las dos casillas:
//
//   · "Modificar productos ya existentes": cada producto de la planilla se cruza con los de la tienda (por
//     identificador de URL o por nombre). Destildada, los que ya existen se sacan; tildada, van aparte para
//     que el asistente los EDITE en vez de crearlos de nuevo (si no, quedaban duplicados).
//   · "Actualizar stock": destildada, la columna Stock se vacía (no se toca el stock de nadie).
//
// La planilla puede venir en el formato de Tiendanube: un producto ocupa varias filas (una por variante) y
// las que siguen a la primera tienen el Nombre vacío y el mismo identificador de URL.

export interface ExistenteEnLaTienda {
  idExterno: string;
  nombre: string;
  url: string | null;
}

export interface ProductoDeLaPlanilla<T extends ExistenteEnLaTienda = ExistenteEnLaTienda> {
  nombre: string;
  /** Sus filas (la primera con los datos del producto, el resto variantes). */
  filas: string[][];
  /** El de la tienda, si ya está. */
  existente: T | null;
  /** Sólo se lo reconoce por el nombre y en la tienda hay más de uno así: no se sabe cuál es. */
  ambiguo: boolean;
}

export interface PlanillaAnalizada<T extends ExistenteEnLaTienda = ExistenteEnLaTienda> {
  encabezado: string[];
  productos: ProductoDeLaPlanilla<T>[];
  /** -1 si la planilla no tiene columna de stock. */
  columnaStock: number;
}

const normalizar = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const columna = (encabezado: string[], ...nombres: string[]) => {
  const buscados = nombres.map(normalizar);
  return encabezado.findIndex((h) => buscados.includes(normalizar(h)));
};

/** El identificador de URL de un producto de la tienda: el último tramo de su link. */
export function identificadorDeUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const partes = new URL(url).pathname.split('/').filter(Boolean);
    return partes.length > 0 ? decodeURIComponent(partes[partes.length - 1]!).toLowerCase() : null;
  } catch {
    return null;
  }
}

export function analizarPlanilla<T extends ExistenteEnLaTienda>(filas: string[][], tienda: readonly T[]): PlanillaAnalizada<T> {
  const sinVacias = filas.filter((f) => f.some((c) => (c ?? '').trim() !== ''));
  const [encabezado = [], ...cuerpo] = sinVacias;
  const iUrl = columna(encabezado, 'Identificador de URL', 'Identificador', 'URL', 'Handle');
  const iNombre = columna(encabezado, 'Nombre', 'Producto', 'Nombre del producto', 'Título', 'Titulo');
  const columnaStock = columna(encabezado, 'Stock', 'Cantidad', 'Inventario');
  // Los de nuestra planilla de Exportar: el id en la tienda y el link (review Greptile de #232). Ganan sobre el
  // nombre, que puede haberse cambiado en la planilla o repetirse en la tienda.
  const iId = columna(encabezado, 'ID en la tienda', 'ID');
  const iLink = columna(encabezado, 'Link', 'URL del producto');

  const porId = new Map<string, T>();
  const porUrl = new Map<string, T>();
  const porNombre = new Map<string, T | null>();
  for (const p of tienda) {
    porId.set(p.idExterno, p);
    const id = identificadorDeUrl(p.url);
    if (id) porUrl.set(id, p);
    // Dos con el mismo nombre: por nombre no se sabe cuál es, así que no se ata a ninguno.
    const n = normalizar(p.nombre);
    porNombre.set(n, porNombre.has(n) ? null : p);
  }

  const productos: ProductoDeLaPlanilla<T>[] = [];
  let actual: (ProductoDeLaPlanilla<T> & { clave: string }) | null = null;
  for (const fila of cuerpo) {
    const url = iUrl >= 0 ? (fila[iUrl] ?? '').trim().toLowerCase() : '';
    const nombre = iNombre >= 0 ? (fila[iNombre] ?? '').trim() : '';
    // Una fila sin nombre es otra variante del producto de arriba (si tiene su mismo identificador, o no tiene).
    const continua = actual && !nombre && (!url || url === actual.clave);
    if (continua) {
      actual!.filas.push(fila);
      continue;
    }
    const idEnLaTienda = iId >= 0 ? (fila[iId] ?? '').trim() : '';
    const delLink = iLink >= 0 ? identificadorDeUrl((fila[iLink] ?? '').trim() || null) : null;
    const existente: T | null =
      (idEnLaTienda && porId.get(idEnLaTienda)) ||
      (url && porUrl.get(url)) ||
      (delLink && porUrl.get(delLink)) ||
      (nombre && porNombre.get(normalizar(nombre))) ||
      null;
    // Un nombre repetido en la tienda no es "nuevo": crearlo lo duplicaría (review Greptile de #232).
    const ambiguo: boolean = !existente && !!nombre && porNombre.has(normalizar(nombre)) && porNombre.get(normalizar(nombre)) === null;
    actual = { clave: url || normalizar(nombre), nombre: nombre || url, filas: [fila], existente, ambiguo };
    productos.push(actual);
  }
  return { encabezado, productos: productos.map(({ nombre, filas: f, existente, ambiguo }) => ({ nombre, filas: f, existente, ambiguo })), columnaStock };
}

export interface PlanillaParaImportar<T extends ExistenteEnLaTienda = ExistenteEnLaTienda> {
  /** Los que se crean: encabezado + filas. Vacío (sólo encabezado) si no hay. */
  nuevos: string[][];
  /** Los que ya están y se modifican: encabezado + filas. */
  aModificar: string[][];
  /** Los productos de la tienda que se modifican, en el orden de la planilla. */
  existentes: T[];
  cuantosNuevos: number;
  /** Cuántas filas ocupa cada producto (el producto y sus variantes), en el orden de `nuevos` y `aModificar`. */
  filasPorNuevo: number[];
  filasPorExistente: number[];
  /** Los que ya estaban y se dejan como están (casilla destildada). */
  salteados: number;
  /** Los que no se importan porque su nombre está repetido en la tienda y no traen id ni link. */
  ambiguos: string[];
}

export function armarParaImportar<T extends ExistenteEnLaTienda>(
  p: PlanillaAnalizada<T>,
  opciones: { modificar: boolean; stock: boolean },
): PlanillaParaImportar<T> {
  const limpiar = (fila: string[]) =>
    !opciones.stock && p.columnaStock >= 0 ? fila.map((c, i) => (i === p.columnaStock ? '' : c)) : fila;
  const nuevos = p.productos.filter((x) => !x.existente && !x.ambiguo);
  const yaEstan = p.productos.filter((x) => x.existente);
  const aModificar = opciones.modificar ? yaEstan : [];
  return {
    nuevos: [p.encabezado, ...nuevos.flatMap((x) => x.filas.map(limpiar))],
    aModificar: [p.encabezado, ...aModificar.flatMap((x) => x.filas.map(limpiar))],
    existentes: aModificar.map((x) => x.existente!),
    cuantosNuevos: nuevos.length,
    filasPorNuevo: nuevos.map((x) => x.filas.length),
    filasPorExistente: aModificar.map((x) => x.filas.length),
    salteados: opciones.modificar ? 0 : yaEstan.length,
    ambiguos: p.productos.filter((x) => x.ambiguo).map((x) => x.nombre),
  };
}
